import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trash2, Archive, CheckCircle, CircleQuestionMark , XCircle, ArrowLeft, Plus, Gamepad2, Save, X, Calendar, Settings, Edit2, Users } from 'lucide-react';
import pb from '../pocketbase';

const DAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const WEEK_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

const pad2 = (n) => String(n).padStart(2, '0');
const toISODate = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const formatShortDate = (date) => `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}`;

const getMondayOfWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const dow = d.getDay(); // 0 = dimanche .. 6 = samedi
    const diff = dow === 0 ? -6 : 1 - dow;
    d.setDate(d.getDate() + diff);
    return d;
};

// Semaine du jour + les 4 prochaines
const getWeekOptions = (today = new Date()) => {
    const thisMonday = getMondayOfWeek(today);
    const options = [];
    for (let i = 0; i < 5; i++) {
        const monday = addDays(thisMonday, i * 7);
        const sunday = addDays(monday, 6);
        options.push({
            key: toISODate(monday),
            monday,
            label: `Semaine du ${formatShortDate(monday)} au ${formatShortDate(sunday)}`,
        });
    }
    return options;
};

// La semaine précédant la semaine en cours — proposée à part (séparateur) dans le
// sélecteur, pour pouvoir revenir en arrière juste après un changement de semaine.
const getPreviousWeekOption = (today = new Date()) => {
    const monday = addDays(getMondayOfWeek(today), -7);
    const sunday = addDays(monday, 6);
    return {
        key: toISODate(monday),
        monday,
        label: `Semaine du ${formatShortDate(monday)} au ${formatShortDate(sunday)} (précédente)`,
    };
};

// week_availabilities peut être soit l'ancien format plat { participant: { lundi: ... } },
// soit le nouveau format imbriqué par semaine { "2026-10-26": { participant: { lundi: ... } } }
const isNestedByWeek = (weekAvailabilities) => {
    const keys = Object.keys(weekAvailabilities || {});
    return keys.length > 0 && keys.every(k => WEEK_KEY_RE.test(k));
};

const normalizeToNested = (weekAvailabilities, fallbackKey) => {
    if (isNestedByWeek(weekAvailabilities)) return { ...weekAvailabilities };
    return { [fallbackKey]: weekAvailabilities || {} };
};

// Jours fériés français (dates fixes + calcul de Pâques — algorithme de Meeus/Jones/Butcher)
const getEasterDate = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
};

const getFrenchHolidays = (year) => {
    const easter = getEasterDate(year);
    return [
        { date: new Date(year, 0, 1), label: "Jour de l'an" },
        { date: addDays(easter, 1), label: 'Lundi de Pâques' },
        { date: new Date(year, 4, 1), label: 'Fête du travail' },
        { date: new Date(year, 4, 8), label: 'Victoire 1945' },
        { date: addDays(easter, 39), label: 'Ascension' },
        { date: addDays(easter, 50), label: 'Lundi de Pentecôte' },
        { date: new Date(year, 6, 14), label: 'Fête nationale' },
        { date: new Date(year, 7, 15), label: 'Assomption' },
        { date: new Date(year, 10, 1), label: 'Toussaint' },
        { date: new Date(year, 10, 11), label: 'Armistice' },
        { date: new Date(year, 11, 25), label: 'Noël' },
    ];
};

const getHolidayLabel = (date) => {
    const holiday = getFrenchHolidays(date.getFullYear()).find(h =>
        h.date.getMonth() === date.getMonth() && h.date.getDate() === date.getDate()
    );
    return holiday ? holiday.label : null;
};

const PartyManager = ({ isGlobalAdmin }) => {
    const { partyId } = useParams();
    const navigate = useNavigate();
    const [parties, setParties] = useState([]);
    const [selectedParty, setSelectedParty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [allUsers, setAllUsers] = useState([]);

    // États pour la création d'une nouvelle partie
    const [isCreating, setIsCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newGame, setNewGame] = useState('');
    const [selectedParticipants, setSelectedParticipants] = useState([]);

    // Popup création de comptes
    const [showCreateAccountsPopup, setShowCreateAccountsPopup] = useState(false);
    const [pendingCreate, setPendingCreate] = useState(null);

    // Fonction pour générer le short_id
    const generateShortId = (gameName, createdDate) => {
        const date = new Date(createdDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);
        const gameShort = gameName.replace(/\s+/g, '').slice(0, 8).toLowerCase();
        return `${gameShort}-${day}${month}${year}`;
    };

    const fetchParties = async () => {
        try {
            const records = await pb.collection('parties').getFullList({ sort: '-created' });

            // Mettre à jour les parties sans short_id
            for (const party of records) {
                if (!party.short_id) {
                    const shortId = generateShortId(party.game, party.created);
                    try {
                        await pb.collection('parties').update(party.id, { short_id: shortId });
                        party.short_id = shortId;
                    } catch (err) {
                        console.error(`Erreur mise à jour short_id pour ${party.id}:`, err);
                    }
                }
            }

            setParties(records);

            // Si on a un partyId dans l'URL, charger cette partie
            if (partyId) {
                const party = records.find(p => p.short_id === partyId);
                if (party) {
                    setSelectedParty(party);
                }
            }
        } catch (err) {
            console.error("Erreur chargement parties:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchParties();
        pb.collection('users').getFullList({ sort: 'username' })
            .then(setAllUsers)
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (partyId && parties.length > 0) {
            const party = parties.find(p => p.short_id === partyId);
            if (party) {
                setSelectedParty(party);
            }
        }
    }, [partyId, parties]);

    // FONCTION : Archiver une partie
    const toggleArchiveParty = async (e, party) => {
        e.stopPropagation();
        const isArchived = party.status === 'archived';
        const action = isArchived ? "Désarchiver" : "Archiver";

        if (!window.confirm(`${action} cette partie ?`)) return;

        try {
            await pb.collection('parties').update(party.id, {
                status: isArchived ? 'active' : 'archived'
            });
            fetchParties();
        } catch (err) {
            alert(`Erreur lors de l'action : ${action}`);
        }
    };

    // FONCTION : Supprimer une partie et ses calls (Cascade)
    const deleteParty = async (e, partyId) => {
        e.stopPropagation();
        if (!window.confirm("⚠️ SUPPRESSION DÉFINITIVE ? Cela supprimera aussi tous les calls et disponibilités associés.")) return;
        try {
            const associatedCalls = await pb.collection('calls').getFullList({ filter: `party = "${partyId}"` });
            for (const call of associatedCalls) {
                await pb.collection('calls').delete(call.id);
            }

            // Supprimer les disponibilités
            const associatedAvail = await pb.collection('availabilities').getFullList({ filter: `party = "${partyId}"` });
            for (const avail of associatedAvail) {
                await pb.collection('availabilities').delete(avail.id);
            }

            await pb.collection('parties').delete(partyId);
            fetchParties();
            navigate('/parties');
        } catch (err) {
            alert("Erreur lors de la suppression");
        }
    };

    const handleCreateParty = async (e) => {
        e.preventDefault();
        if (selectedParticipants.length === 0) { alert('Ajoutez au moins un participant.'); return; }
        const guests = selectedParticipants.filter(p => !p.userId);
        if (guests.length > 0) {
            setPendingCreate({ participants: selectedParticipants });
            setShowCreateAccountsPopup(true);
        } else {
            await doCreateParty(selectedParticipants);
        }
    };

    const doCreateParty = async (participants) => {
        const participantsArray = participants.map(p => p.username);
        const participantUserIds = {};
        participants.forEach(p => { if (p.userId) participantUserIds[p.username] = p.userId; });
        try {
            const now = new Date();
            const newParty = await pb.collection('parties').create({
                name: newName, game: newGame, participants: participantsArray,
                status: 'active', short_id: generateShortId(newGame, now),
                participantUserIds, created_by: pb.authStore.model?.id || ''
            });
            const initialAvailabilities = {};
            participantsArray.forEach(p => {
                initialAvailabilities[p] = { lundi: null, mardi: null, mercredi: null, jeudi: null, vendredi: null, samedi: null, dimanche: null };
            });
            await pb.collection('availabilities').create({ party: newParty.id, week_availabilities: initialAvailabilities });
            setNewName(''); setNewGame(''); setSelectedParticipants([]);
            setIsCreating(false); fetchParties();
        } catch (err) {
            console.error("Erreur création:", err);
            alert("Erreur création : vérifiez les API Rules dans PocketBase");
        }
    };

    const handleCreateAccountsConfirm = async (guestsToCreate) => {
        setShowCreateAccountsPopup(false);
        let participants = [...pendingCreate.participants];
        for (const name of guestsToCreate) {
            try {
                const newUser = await pb.collection('users').create({
                    username: name, password: `${name}123`, passwordConfirm: `${name}123`,
                    name, isAdmin: false, rawPassword: `${name}123`
                });
                participants = participants.map(p => p.username === name ? { ...p, userId: newUser.id } : p);
                setAllUsers(prev => [...prev, newUser].sort((a, b) => a.username.localeCompare(b.username)));
            } catch (err) { console.error(`Erreur création compte ${name}:`, err); }
        }
        await doCreateParty(participants);
        setPendingCreate(null);
    };

    const handlePartyClick = (party) => {
        navigate(`/parties/${party.short_id}`);
        setSelectedParty(party);
    };

    if (loading) return <div style={{padding: '20px', color: 'white'}}>Chargement...</div>;

    const displayedParties = parties.filter(p => isGlobalAdmin ? true : p.status !== 'archived');

    if (!selectedParty) {
        return (
            <div style={styles.mainContainer}>
                <div style={styles.header} className="fade-in-up">
                    <h2 style={styles.pageTitle} className="tvs-title-gradient">
                        {isGlobalAdmin ? '🛠️ Gestion des Parties (Admin)' : '🎮 Parties Disponibles'}
                    </h2>
                    {!isCreating && (
                        <button onClick={() => setIsCreating(true)} style={styles.addBtn} className="tvs-btn-lift">
                            <Plus size={18}/> Nouvelle Partie
                        </button>
                    )}
                </div>

                {isCreating && (
                    <form onSubmit={handleCreateParty} style={styles.createForm} className="fade-in-up">
                        <h3 style={{marginTop: 0, color: '#ff4444'}}>Nouvelle Organisation</h3>
                        <div style={styles.formGroup}>
                            <input
                                placeholder="Nom (ex: No CB zaza)"
                                value={newName}
                                onChange={e => setNewName(e.target.value)}
                                style={styles.input}
                                className="tvs-input"
                                required
                            />
                            <input
                                placeholder="Jeu (ex: Victoria3)"
                                value={newGame}
                                onChange={e => setNewGame(e.target.value)}
                                style={styles.input}
                                className="tvs-input"
                                required
                            />
                        </div>
                        <ParticipantSelector
                            allUsers={allUsers}
                            selected={selectedParticipants}
                            onChange={setSelectedParticipants}
                        />
                        <div style={{display: 'flex', gap: '10px', marginTop: '15px'}}>
                            <button type="submit" style={styles.addBtn} className="tvs-btn-lift">
                                <Save size={18}/> Enregistrer
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsCreating(false)}
                                style={{...styles.addBtn, background: '#444'}}
                                className="tvs-btn-lift"
                            >
                                <X size={18}/> Annuler
                            </button>
                        </div>
                    </form>
                )}

                {showCreateAccountsPopup && pendingCreate && (
                    <CreateAccountsPopup
                        guests={pendingCreate.participants.filter(p => !p.userId).map(p => p.username)}
                        onConfirm={handleCreateAccountsConfirm}
                        onCancel={() => { setShowCreateAccountsPopup(false); setPendingCreate(null); }}
                    />
                )}

                <div style={styles.grid}>
                    {displayedParties.length > 0 ? (
                        displayedParties.map((p, i) => (
                            <div
                                key={p.id}
                                onClick={() => handlePartyClick(p)}
                                className="fade-in-up tvs-card-hover"
                                style={{
                                    ...styles.card,
                                    opacity: p.status === 'archived' ? 0.6 : 1,
                                    border: p.status === 'archived' ? '1px dashed #555' : '1px solid #333',
                                    animationDelay: `${Math.min(i, 8) * 60}ms`,
                                }}
                            >
                                <Gamepad2
                                    size={32}
                                    color={p.status === 'archived' ? "#555" : "#ff4444"}
                                    style={{marginBottom: '10px'}}
                                />
                                <h3 style={{margin: '5px 0'}}>
                                    {p.name} {p.status === 'archived' && '(Archivé)'}
                                </h3>
                                <p style={{color: '#888', fontSize: '0.9rem'}}>{p.game}</p>
                                <p style={{color: '#666', fontSize: '0.75rem', marginTop: '8px'}}>
                                    ID: {p.short_id}
                                </p>

                                {isGlobalAdmin && (
                                    <div style={styles.adminActions}>
                                        <button
                                            onClick={(e) => toggleArchiveParty(e, p)}
                                            style={{
                                                ...styles.miniBtn,
                                                background: p.status === 'archived' ? '#4a5568' : '#444'
                                            }}
                                            title={p.status === 'archived' ? "Désarchiver" : "Archiver"}
                                        >
                                            {p.status === 'archived' ? <Plus size={14} /> : <Archive size={14} />}
                                        </button>

                                        <button
                                            onClick={(e) => deleteParty(e, p.id)}
                                            style={{...styles.miniBtn, background: '#833'}}
                                            title="Supprimer"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div style={styles.emptyState}>Aucune partie active.</div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <PartyDetail
            party={selectedParty}
            onBack={() => {
                navigate('/parties');
                setSelectedParty(null);
            }}
            isGlobalAdmin={isGlobalAdmin}
            allUsers={allUsers}
            onPartyUpdated={(updated) => { setSelectedParty(updated); fetchParties(); }}
        />
    );
};

const PartyDetail = ({ party, onBack, isGlobalAdmin, allUsers, onPartyUpdated }) => {
    const currentUserId = pb.authStore.model?.id;
    const isPartyAdmin = isGlobalAdmin || (party?.created_by && party.created_by === currentUserId);

    const [calls, setCalls] = useState([]);
    const [newDate, setNewDate] = useState('');
    const [availabilities, setAvailabilities] = useState(null);
    const [showAvailModal, setShowAvailModal] = useState(false);
    const [botToken, setBotToken] = useState(null);
    const [botActions, setBotActions] = useState({});
    const [allChannels, setAllChannels] = useState([]);
    const [defaultChannelId, setDefaultChannelId] = useState('');
    const [partyChannelId, setPartyChannelId] = useState(party?.discord_channel_id || '');

    // Édition globale de la partie (nom, jeu, participants)
    const [showEditModal, setShowEditModal] = useState(false);
    const [editName, setEditName] = useState('');
    const [editGame, setEditGame] = useState('');
    const [editParticipants, setEditParticipants] = useState([]);
    const [savingEdit, setSavingEdit] = useState(false);
    const [showEditAccountsPopup, setShowEditAccountsPopup] = useState(false);
    const [pendingEditParticipants, setPendingEditParticipants] = useState(null);

    // Création rapide d'un call depuis une pastille de disponibilité commune (admin uniquement)
    const [callPopupDay, setCallPopupDay] = useState(null);
    const [callPopupTime, setCallPopupTime] = useState('20:00');
    const [creatingCall, setCreatingCall] = useState(false);

    // Semaine sélectionnée pour les disponibilités — c'est un paramétrage de la partie
    // (stocké dans parties.current_week), forcé pour tout le monde. Seul un admin peut
    // la changer ; tous les joueurs remplissent et consultent les dispos pour cette semaine.
    const weekOptions = React.useMemo(() => getWeekOptions(), []);
    const previousWeekOption = React.useMemo(() => getPreviousWeekOption(), []);
    const selectableWeeks = React.useMemo(() => [previousWeekOption, ...weekOptions], [previousWeekOption, weekOptions]);
    const isValidWeekKey = (key) => selectableWeeks.some(w => w.key === key);
    const [selectedWeekKey, setSelectedWeekKey] = useState(
        isValidWeekKey(party?.current_week) ? party.current_week : weekOptions[0].key
    );

    useEffect(() => {
        const fallbackKey = weekOptions[0].key;
        if (isValidWeekKey(party?.current_week)) {
            setSelectedWeekKey(party.current_week);
            return;
        }
        // party.current_week est absent ou n'est plus dans la fenêtre (semaine en cours + 4 suivantes) —
        // le dropdown retombe sur la semaine réelle en cours. Un admin la persiste en base pour que
        // le bot Discord (qui lit directement parties.current_week) reste synchronisé avec l'affichage.
        setSelectedWeekKey(fallbackKey);
        if (isPartyAdmin && party?.id && party.current_week !== fallbackKey) {
            pb.collection('parties').update(party.id, { current_week: fallbackKey })
                .then(updated => { if (onPartyUpdated) onPartyUpdated(updated); })
                .catch(err => console.error('Erreur sync automatique de la semaine:', err));
        }
    }, [party?.id, party?.current_week, isPartyAdmin]);

    const handleChangeWeek = async (weekKey) => {
        if (!party?.id) return;
        setSelectedWeekKey(weekKey);
        try {
            const updated = await pb.collection('parties').update(party.id, { current_week: weekKey });
            if (onPartyUpdated) onPartyUpdated(updated);
        } catch (err) {
            console.error('Erreur sauvegarde semaine sélectionnée:', err);
        }
    };

    const sortedParticipants = [...(party?.participants || [])].sort((a, b) => a.localeCompare(b));

    const fetchCalls = async () => {
        if (!party?.id) return;
        try {
            const records = await pb.collection('calls').getFullList({
                filter: `party = "${party.id}"`,
                sort: '-date',
            });
            setCalls(records);
        } catch (err) {
            console.error("Erreur calls:", err);
        }
    };

    const fetchAvailabilities = async () => {
        if (!party?.id) return;
        try {
            const records = await pb.collection('availabilities').getFullList({
                filter: `party = "${party.id}"`
            });

            if (records.length > 0) {
                setAvailabilities(records[0]);
            } else {
                // Créer une entrée vide si elle n'existe pas
                const initialAvailabilities = {};
                party.participants.forEach(participant => {
                    initialAvailabilities[participant] = {
                        lundi: null,
                        mardi: null,
                        mercredi: null,
                        jeudi: null,
                        vendredi: null,
                        samedi: null,
                        dimanche: null
                    };
                });

                const newAvail = await pb.collection('availabilities').create({
                    party: party.id,
                    week_availabilities: initialAvailabilities
                });
                setAvailabilities(newAvail);
            }
        } catch (err) {
            console.error("Erreur availabilities:", err);
        }
    };

    useEffect(() => {
        fetchCalls();
        fetchAvailabilities();
        if (isPartyAdmin) {
            pb.collection('settings').getFullList({ filter: 'key = "bot_api_token" || key = "default_channel_id"' })
                .then(async records => {
                    const tokenRec = records.find(r => r.key === 'bot_api_token');
                    const defChanRec = records.find(r => r.key === 'default_channel_id');
                    if (defChanRec?.value) setDefaultChannelId(defChanRec.value);
                    if (tokenRec?.value) {
                        const token = tokenRec.value;
                        setBotToken(token);
                        const res = await fetch('/bot-api/channels', { headers: { 'x-bot-token': token } }).catch(() => null);
                        if (res?.ok) setAllChannels(await res.json());
                    }
                })
                .catch(() => {});
        }
    }, [party?.id, isPartyAdmin]);

    useEffect(() => {
        if (!party?.id) return;
        pb.collection('calls').subscribe('*', (e) => {
            if (e.record.party !== party.id) return;
            if (e.action === 'delete') {
                setCalls(prev => prev.filter(c => c.id !== e.record.id));
            } else if (e.action === 'create') {
                setCalls(prev => [...prev, e.record].sort((a, b) => new Date(b.date) - new Date(a.date)));
            } else {
                setCalls(prev => prev.map(c => c.id === e.record.id ? e.record : c));
            }
        });
        return () => { pb.collection('calls').unsubscribe('*'); };
    }, [party?.id]);

    useEffect(() => {
        if (!availabilities?.id) return;
        pb.collection('availabilities').subscribe(availabilities.id, (e) => {
            if (e.action === 'update') setAvailabilities(e.record);
        });
        return () => { pb.collection('availabilities').unsubscribe(availabilities.id); };
    }, [availabilities?.id]);

    // Propage en direct le changement de semaine (ou d'autres éditions) de la partie
    // à tous les joueurs connectés — la semaine sélectionnée est un paramétrage partagé.
    useEffect(() => {
        if (!party?.id) return;
        pb.collection('parties').subscribe(party.id, (e) => {
            if (e.action === 'update' && onPartyUpdated) onPartyUpdated(e.record);
        });
        return () => { pb.collection('parties').unsubscribe(party.id); };
    }, [party?.id]);

    const addCall = async () => {
        if (!newDate || !party?.participants) return;
        const initialPresence = {};
        party.participants.forEach(p => initialPresence[p] = 'notSet');

        try {
            await pb.collection('calls').create({
                party: party.id,
                date: new Date(newDate).toISOString(),
                presence: initialPresence
            });
            setNewDate('');
            fetchCalls();
        } catch (err) {
            alert("Erreur lors de la création du call");
        }
    };

    const togglePresence = async (callId, participant) => {
        try {
            const latest = await pb.collection('calls').getOne(callId);
            const currentStatus = latest.presence?.[participant];

            let nextStatus;
            if (currentStatus === true) nextStatus = false;
            else if (currentStatus === false) nextStatus = 'notSet';
            else nextStatus = true;

            const updatedPresence = { ...latest.presence, [participant]: nextStatus };
            await pb.collection('calls').update(callId, { presence: updatedPresence });
        } catch (err) {
            console.error("Erreur sync présence:", err);
            fetchCalls();
        }
    };

    const toggleAvailability = async (participant, day, weekKey) => {
        if (!availabilities) return;
        const effectiveWeekKey = weekKey || selectedWeekKey;

        try {
            const latest = await pb.collection('availabilities').getOne(availabilities.id);
            const nested = normalizeToNested(latest.week_availabilities, weekOptions[0].key);
            const weekGrid = { ...(nested[effectiveWeekKey] || {}) };
            const participantGrid = { ...(weekGrid[participant] || {
                lundi: null, mardi: null, mercredi: null, jeudi: null, vendredi: null, samedi: null, dimanche: null
            }) };
            const current = participantGrid[day] ?? null;
            participantGrid[day] = current === null ? true : current === true ? false : null;
            weekGrid[participant] = participantGrid;
            nested[effectiveWeekKey] = weekGrid;

            const result = await pb.collection('availabilities').update(latest.id, {
                week_availabilities: nested
            });
            setAvailabilities(result);
        } catch (err) {
            console.error("Erreur update availabilities:", err);
        }
    };

    const getCommonAvailableDays = () => {
        if (!availabilities || !party?.participants) return [];

        const nested = normalizeToNested(availabilities.week_availabilities, weekOptions[0].key);
        const weekGrid = nested[selectedWeekKey] || {};
        const commonDays = [];

        for (const day of DAYS) {
            const allAvailable = party.participants.every(participant => {
                return weekGrid[participant]?.[day] === true;
            });

            if (allAvailable) {
                commonDays.push(day);
            }
        }

        return commonDays;
    };

    // ---- Édition globale de la partie ----

    const openEditModal = () => {
        setEditName(party?.name || '');
        setEditGame(party?.game || '');
        setEditParticipants(
            (party?.participants || []).map(name => ({
                username: name,
                userId: party?.participantUserIds?.[name] || null,
            }))
        );
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        if (savingEdit) return;
        setShowEditModal(false);
    };

    const handleEditSubmit = (e) => {
        e.preventDefault();
        if (editParticipants.length === 0) { alert('Ajoutez au moins un participant.'); return; }
        const guests = editParticipants.filter(p => !p.userId);
        if (guests.length > 0) {
            setPendingEditParticipants(editParticipants);
            setShowEditAccountsPopup(true);
        } else {
            doUpdateParty(editParticipants);
        }
    };

    const handleEditAccountsConfirm = async (guestsToCreate) => {
        setShowEditAccountsPopup(false);
        let participants = [...pendingEditParticipants];
        for (const name of guestsToCreate) {
            try {
                const newUser = await pb.collection('users').create({
                    username: name, password: `${name}123`, passwordConfirm: `${name}123`,
                    name, isAdmin: false, rawPassword: `${name}123`
                });
                participants = participants.map(p => p.username === name ? { ...p, userId: newUser.id } : p);
            } catch (err) { console.error(`Erreur création compte ${name}:`, err); }
        }
        await doUpdateParty(participants);
        setPendingEditParticipants(null);
    };

    const doUpdateParty = async (participants) => {
        setSavingEdit(true);
        try {
            const participantsArray = participants.map(p => p.username);
            const participantUserIds = {};
            participants.forEach(p => { if (p.userId) participantUserIds[p.username] = p.userId; });

            const oldParticipants = party?.participants || [];
            const added = participantsArray.filter(p => !oldParticipants.includes(p));
            const removed = oldParticipants.filter(p => !participantsArray.includes(p));

            const updated = await pb.collection('parties').update(party.id, {
                name: editName,
                game: editGame,
                participants: participantsArray,
                participantUserIds,
            });

            // Synchroniser les disponibilités (ajout/retrait de participants) sur toutes les semaines connues
            if (availabilities && (added.length > 0 || removed.length > 0)) {
                const currentWeekKey = toISODate(getMondayOfWeek(new Date()));
                const nested = normalizeToNested(availabilities.week_availabilities, currentWeekKey);
                const emptyGrid = { lundi: null, mardi: null, mercredi: null, jeudi: null, vendredi: null, samedi: null, dimanche: null };
                Object.keys(nested).forEach(weekKey => {
                    added.forEach(name => { nested[weekKey][name] = { ...emptyGrid }; });
                    removed.forEach(name => { delete nested[weekKey][name]; });
                });
                await pb.collection('availabilities').update(availabilities.id, { week_availabilities: nested });
            }

            // Synchroniser la présence sur les calls existants
            if (added.length > 0 || removed.length > 0) {
                for (const call of calls) {
                    const updatedPresence = { ...call.presence };
                    let changed = false;
                    added.forEach(name => { if (!(name in updatedPresence)) { updatedPresence[name] = 'notSet'; changed = true; } });
                    removed.forEach(name => { if (name in updatedPresence) { delete updatedPresence[name]; changed = true; } });
                    if (changed) await pb.collection('calls').update(call.id, { presence: updatedPresence });
                }
            }

            setShowEditModal(false);
            fetchAvailabilities();
            fetchCalls();
            if (onPartyUpdated) onPartyUpdated(updated);
        } catch (err) {
            console.error('Erreur édition partie:', err);
            alert("Erreur lors de la modification de la partie");
        } finally {
            setSavingEdit(false);
        }
    };

    const callBotApi = async (endpoint) => {
        if (!botToken) return;
        setBotActions(a => ({ ...a, [endpoint]: 'loading' }));
        try {
            const res = await fetch(`/bot-api/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-bot-token': botToken },
                body: JSON.stringify({ party_short_id: party.short_id }),
            });
            const data = await res.json();
            setBotActions(a => ({ ...a, [endpoint]: res.ok ? 'success' : 'error' }));
            setTimeout(() => setBotActions(a => ({ ...a, [endpoint]: null })), 3000);
            if (!res.ok) console.error(`Bot API ${endpoint}:`, data.error);
        } catch (e) {
            setBotActions(a => ({ ...a, [endpoint]: 'error' }));
            setTimeout(() => setBotActions(a => ({ ...a, [endpoint]: null })), 3000);
        }
    };

    const now = new Date().getTime();
    const currentCalls = calls.filter(c => new Date(c.date).getTime() >= now);
    const pastCalls = calls.filter(c => new Date(c.date).getTime() < now);
    const commonDays = getCommonAvailableDays();
    const selectedWeek = selectableWeeks.find(w => w.key === selectedWeekKey) || weekOptions[0];

    const openCallPopup = (day) => {
        setCallPopupDay(day);
        setCallPopupTime('20:00');
    };

    const closeCallPopup = () => {
        if (creatingCall) return;
        setCallPopupDay(null);
    };

    const handleCreateCallForDay = async (e) => {
        e.preventDefault();
        if (!callPopupDay || !party?.participants) return;

        const dayIndex = DAYS.indexOf(callPopupDay);
        const targetDate = addDays(selectedWeek.monday, dayIndex);
        const [hh, mm] = callPopupTime.split(':').map(Number);
        targetDate.setHours(hh, mm, 0, 0);

        const initialPresence = {};
        party.participants.forEach(p => initialPresence[p] = 'notSet');

        setCreatingCall(true);
        try {
            await pb.collection('calls').create({
                party: party.id,
                date: targetDate.toISOString(),
                presence: initialPresence
            });
            setCallPopupDay(null);
            fetchCalls();
        } catch (err) {
            alert("Erreur lors de la création du call");
        } finally {
            setCreatingCall(false);
        }
    };

    return (
        <div style={styles.detailContainer}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <button onClick={onBack} style={styles.backBtn} className="tvs-btn-lift">
                    <ArrowLeft size={18}/> Retour aux parties
                </button>
                {isPartyAdmin && (
                    <button onClick={openEditModal} style={styles.settingsBtn} className="tvs-btn-lift">
                        <Edit2 size={16}/> Éditer la partie
                    </button>
                )}
            </div>
            <h1 style={styles.title} className="fade-in-up">
                {party?.name} <span style={styles.subtitle}>({party?.game})</span>
            </h1>

            {sortedParticipants.length > 0 && (
                <div style={styles.playersRow} className="fade-in-up">
                    <Users size={14} color="#666" />
                    <span style={styles.playersText}>{sortedParticipants.join(' · ')}</span>
                </div>
            )}

            {/* NOUVEAU CALL + DISPONIBILITÉS SUR LA MÊME LIGNE */}
            <div style={styles.topRowContainer}>
                {/* NOUVEAU CALL - VERSION COMPACTE */}
                <section style={styles.callSection} className="fade-in-up">
                    <h3 style={styles.sectionTitle}>🚀 Nouveau Call</h3>
                    <div style={styles.compactForm}>
                        <input
                            type="datetime-local"
                            value={newDate}
                            onChange={(e) => setNewDate(e.target.value)}
                            style={styles.input}
                            className="tvs-input"
                        />
                        <button onClick={addCall} style={styles.addBtn} className="tvs-btn-lift">
                            <Plus size={18}/> Ajouter
                        </button>
                    </div>
                </section>

                {/* ENCART DISPONIBILITÉS */}
                <section style={{...styles.availSection, animationDelay: '80ms'}} className="fade-in-up">
                    <div style={styles.availHeader}>
                        <h3 style={styles.sectionTitle}>📅 Disponibilités</h3>
                        <button onClick={() => setShowAvailModal(true)} style={styles.settingsBtn} className="tvs-btn-lift">
                            <Settings size={18}/> Gérer
                        </button>
                    </div>
                    <div style={styles.availDays}>
                        {commonDays.length > 0 ? (
                            commonDays.map(day => (
                                <div key={day} style={styles.dayBadge}>
                                    <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                                    {isPartyAdmin && (
                                        <button
                                            onClick={() => openCallPopup(day)}
                                            style={styles.dayBadgeAddBtn}
                                            className="tvs-btn-lift"
                                            title={`Créer un call pour ${day}`}
                                            type="button"
                                        >
                                            <Calendar size={13}/>
                                        </button>
                                    )}
                                </div>
                            ))
                        ) : (
                            <p style={{color: '#888', textAlign: 'center', margin: 0}}>
                                Aucun jour commun disponible
                            </p>
                        )}
                    </div>
                </section>
            </div>

            <section style={{...styles.section, animationDelay: '140ms'}} className="fade-in-up">
                <h3 style={styles.sectionTitle}>🚀 Calls à venir</h3>
                {currentCalls.length > 0 ? (
                    <PresenceTable
                        participants={sortedParticipants}
                        calls={currentCalls}
                        onToggle={togglePresence}
                        onDelete={fetchCalls}
                        interactive={true}
                        adminMode={isPartyAdmin}
                        participantUserIds={party?.participantUserIds || null}
                    />
                ) : <p style={{color: '#666'}}>Aucun rendez-vous de prévu.</p>}
            </section>

            <section style={styles.section}>
                <h3 style={styles.sectionTitle}>📝 Historique</h3>
                {pastCalls.length > 0 ? (
                    <PresenceTable
                        participants={sortedParticipants}
                        calls={pastCalls}
                        interactive={false}
                        onDelete={fetchCalls}
                        adminMode={isPartyAdmin}
                    />
                ) : <p style={{color: '#666'}}>L'historique est vide.</p>}
            </section>

            {/* ACTIONS DISCORD — party admin uniquement */}
            {isPartyAdmin && botToken && (
                <section style={styles.section}>
                    <div style={discordActionsStyle.header}>
                        <h3 style={{ ...styles.sectionTitle, margin: 0 }}>🤖 Actions Discord</h3>
                        <ChannelSelector
                            partyId={party?.id}
                            partyChannelId={partyChannelId}
                            defaultChannelId={defaultChannelId}
                            allChannels={allChannels}
                            onSave={setPartyChannelId}
                        />
                    </div>
                    <div style={discordActionsStyle.row}>
                        <BotActionButton
                            label="Rappel prochain call"
                            description="Ping les retardataires, confirme si tout le monde est prêt, ou annonce l'annulation"
                            state={botActions['remind-dispos']}
                            onClick={() => callBotApi('remind-dispos')}
                        />
                        <BotActionButton
                            label="Disponibilités prochain call"
                            description="Poste l'embed du prochain call avec les présences dans Discord"
                            state={botActions['next-call']}
                            onClick={() => callBotApi('next-call')}
                        />
                        <BotActionButton
                            label="Disponibilités"
                            description="Poste le tableau des dispos de la semaine dans Discord"
                            state={botActions['show-dispos']}
                            onClick={() => callBotApi('show-dispos')}
                        />
                    </div>
                </section>
            )}

            {/* MODAL DISPONIBILITÉS */}
            {showAvailModal && (
                <AvailabilityModal
                    participants={sortedParticipants}
                    availabilities={availabilities}
                    onToggle={toggleAvailability}
                    onClose={() => setShowAvailModal(false)}
                    participantUserIds={party?.participantUserIds || null}
                    isAdmin={isPartyAdmin}
                    weekOptions={weekOptions}
                    previousWeekOption={previousWeekOption}
                    selectedWeekKey={selectedWeekKey}
                    onChangeWeek={handleChangeWeek}
                />
            )}

            {/* MODAL ÉDITION GLOBALE DE LA PARTIE */}
            {showEditModal && (
                <div style={styles.modalOverlay} onClick={closeEditModal}>
                    <div style={{...styles.modalContent, maxWidth: '640px'}} className="fade-in-up" onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h3 style={{margin: 0}} className="tvs-title-gradient">Éditer la partie</h3>
                            <button onClick={closeEditModal} style={styles.closeBtn} className="tvs-btn-lift" disabled={savingEdit}>
                                <X size={24}/>
                            </button>
                        </div>
                        <form onSubmit={handleEditSubmit} style={styles.modalBody}>
                            <div style={styles.formGroup}>
                                <input
                                    placeholder="Nom (ex: No CB zaza)"
                                    value={editName}
                                    onChange={e => setEditName(e.target.value)}
                                    style={styles.input}
                                    className="tvs-input"
                                    required
                                />
                                <input
                                    placeholder="Jeu (ex: Victoria3)"
                                    value={editGame}
                                    onChange={e => setEditGame(e.target.value)}
                                    style={styles.input}
                                    className="tvs-input"
                                    required
                                />
                            </div>
                            <ParticipantSelector
                                allUsers={allUsers}
                                selected={editParticipants}
                                onChange={setEditParticipants}
                            />
                            <div style={{display: 'flex', gap: '10px', marginTop: '15px'}}>
                                <button type="submit" style={styles.addBtn} className="tvs-btn-lift" disabled={savingEdit}>
                                    <Save size={18}/> {savingEdit ? 'Enregistrement...' : 'Enregistrer'}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeEditModal}
                                    style={{...styles.addBtn, background: '#444'}}
                                    className="tvs-btn-lift"
                                    disabled={savingEdit}
                                >
                                    <X size={18}/> Annuler
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showEditAccountsPopup && pendingEditParticipants && (
                <CreateAccountsPopup
                    guests={pendingEditParticipants.filter(p => !p.userId).map(p => p.username)}
                    onConfirm={handleEditAccountsConfirm}
                    onCancel={() => { setShowEditAccountsPopup(false); setPendingEditParticipants(null); }}
                />
            )}

            {/* POPUP CRÉATION RAPIDE D'UN CALL DEPUIS UNE PASTILLE DE DISPONIBILITÉ */}
            {callPopupDay && (
                <div style={styles.modalOverlay} onClick={closeCallPopup}>
                    <div style={{...styles.modalContent, maxWidth: '380px'}} className="fade-in-up" onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h3 style={{margin: 0}} className="tvs-title-gradient">Nouveau call</h3>
                            <button onClick={closeCallPopup} style={styles.closeBtn} className="tvs-btn-lift" disabled={creatingCall}>
                                <X size={24}/>
                            </button>
                        </div>
                        <form onSubmit={handleCreateCallForDay} style={styles.modalBody}>
                            <p style={{color: '#aaa', marginTop: 0}}>
                                {callPopupDay.charAt(0).toUpperCase() + callPopupDay.slice(1)}{' '}
                                {formatShortDate(addDays(selectedWeek.monday, DAYS.indexOf(callPopupDay)))} — à quelle heure ?
                            </p>
                            <input
                                type="time"
                                value={callPopupTime}
                                onChange={e => setCallPopupTime(e.target.value)}
                                style={styles.input}
                                className="tvs-input"
                                required
                                autoFocus
                            />
                            <div style={{display: 'flex', gap: '10px', marginTop: '15px'}}>
                                <button type="submit" style={styles.addBtn} className="tvs-btn-lift" disabled={creatingCall}>
                                    <Save size={18}/> {creatingCall ? 'Création...' : 'Créer le call'}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeCallPopup}
                                    style={{...styles.addBtn, background: '#444'}}
                                    className="tvs-btn-lift"
                                    disabled={creatingCall}
                                >
                                    <X size={18}/> Annuler
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const PresenceTable = ({ adminMode, participants, calls, onToggle, interactive, onDelete, participantUserIds }) => {
    const currentUser = pb.authStore.model;
    const isActualAdmin = adminMode || currentUser?.isAdmin;

    const canToggle = (participant) => {
        if (!interactive) return false;
        if (isActualAdmin) return true;
        if (!participantUserIds || Object.keys(participantUserIds).length === 0) return true;
        return participantUserIds[participant] === currentUser?.id;
    };

    const handleDeleteCall = async (callId) => {
        // Une petite sécurité pour éviter les miss-clicks
        if (window.confirm("Es-tu sûr de vouloir supprimer ce call ? Cette action est irréversible.")) {
            try {
                // Suppression dans PocketBase
                await pb.collection('calls').delete(callId);

                if (onDelete) onDelete();

                console.log("✅ Call supprimé avec succès");
            } catch (error) {
                console.error("❌ Erreur lors de la suppression du call:", error);
                alert("Erreur lors de la suppression");
            }
        }
    };

    return (
        <div style={styles.tableWrapper}>
            <table style={styles.table}>
                <thead>
                <tr>
                    {adminMode && (<th style={styles.th}>Gestion</th>)}
                    <th style={styles.th}>Date / Heure</th>
                    {participants.map(p => <th key={p} style={styles.th}>{p}</th>)}
                </tr>
                </thead>
                <tbody>
                {calls.map(call => (
                    <tr key={call.id} style={styles.tr} className="tvs-row-hover">
                        {adminMode && (
                            <td className={"px-2 py-4 text-center"}>
                                <button
                                    onClick={() => handleDeleteCall(call.id)}
                                    style={{...styles.miniBtn, background: '#833'}}
                                    title="Supprimer ce call">
                                    <Trash2 size={14}/>
                                </button>
                            </td>
                        )}
                        <td style={styles.td}>
                            {(() => {
                                const d = new Date(call.date.replace(' ', 'T'));
                                const dateStr = d.toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
                                const timeStr = d.toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' }).replace(':', 'h');
                                return `${dateStr.charAt(0).toUpperCase()}${dateStr.slice(1)} à ${timeStr}`;
                            })()}
                        </td>
                        {participants.map(p => {
                            const status = call.presence?.[p];
                            const togglable = canToggle(p);
                            return (
                                <td key={p} style={{ ...styles.td, opacity: interactive && !togglable ? 0.35 : 1 }}>
                                    <div
                                        onClick={() => togglable && onToggle(call.id, p)}
                                        style={{
                                            cursor: togglable ? 'pointer' : interactive ? 'not-allowed' : 'default',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            transition: 'transform 0.1s'
                                        }}
                                        onMouseDown={(e) => togglable && (e.currentTarget.style.transform = 'scale(0.8)')}
                                        onMouseUp={(e) => togglable && (e.currentTarget.style.transform = 'scale(1)')}
                                    >
                                        {status === true && <CheckCircle color="#4ade80" size={24}/>}
                                        {status === false && <XCircle color="#f87171" size={24}/>}
                                        {(status === 'notSet' || status === undefined || status === null) &&
                                            <CircleQuestionMark color="#cfb629" size={24}/>
                                        }
                                    </div>
                                </td>
                            )
                        })}
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

const BotActionButton = ({ label, description, state, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const idle = state !== 'loading' && state !== 'success' && state !== 'error';
    const bg = state === 'success' ? '#166534'
             : state === 'error'   ? '#7f1d1d'
             : hovered             ? '#4752c4'
             :                       '#5865f2';
    const shadow = hovered && idle ? '0 4px 16px rgba(88,101,242,0.5)' : '0 2px 6px rgba(0,0,0,0.4)';
    const icon = state === 'loading' ? '⏳' : state === 'success' ? '✅' : state === 'error' ? '❌' : '▶';

    return (
        <button
            onClick={onClick}
            disabled={!idle}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            title={description}
            style={{
                background: bg,
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                padding: '10px 22px',
                cursor: idle ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                boxShadow: shadow,
                transform: hovered && idle ? 'translateY(-2px)' : 'none',
                transition: 'background 0.15s, box-shadow 0.15s, transform 0.15s',
                whiteSpace: 'nowrap',
                opacity: state === 'loading' ? 0.7 : 1,
            }}
        >
            <span>{icon}</span>
            <span>{label}</span>
        </button>
    );
};

const ChannelSelector = ({ partyId, partyChannelId, defaultChannelId, allChannels, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [selected, setSelected] = useState(partyChannelId || '');
    const [saving, setSaving] = useState(false);

    const effectiveId = partyChannelId || defaultChannelId;
    const currentChannel = allChannels.find(c => c.id === effectiveId);
    const isDefault = !partyChannelId;

    const save = async () => {
        setSaving(true);
        try {
            await pb.collection('parties').update(partyId, { discord_channel_id: selected || null });
            onSave(selected);
            setEditing(false);
        } catch (e) {
            console.error('Erreur update channel:', e);
        }
        setSaving(false);
    };

    const cancel = () => { setSelected(partyChannelId || ''); setEditing(false); };

    if (allChannels.length === 0) return null;

    if (editing) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <select
                    value={selected}
                    onChange={e => setSelected(e.target.value)}
                    style={{ background: '#222', border: '1px solid #5865f2', color: 'white', borderRadius: '6px', padding: '5px 8px', fontSize: '0.85rem', outline: 'none' }}
                >
                    <option value="">Par défaut ({allChannels.find(c => c.id === defaultChannelId)?.name || '—'})</option>
                    {allChannels.map(c => (
                        <option key={c.id} value={c.id}>
                            {c.parentName ? `${c.parentName} / ` : ''}#{c.name}
                        </option>
                    ))}
                </select>
                <button onClick={save} disabled={saving} style={chanBtnStyle('#166534')}>✓</button>
                <button onClick={cancel} style={chanBtnStyle('#444')}>✗</button>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#7289da', fontSize: '0.85rem', background: '#1e2030', padding: '4px 10px', borderRadius: '6px', border: '1px solid #3c4070' }}>
                📣 {currentChannel ? `#${currentChannel.name}` : '—'}
                {isDefault && <span style={{ color: '#555', marginLeft: '4px', fontSize: '0.75rem' }}>(défaut)</span>}
            </span>
            <button onClick={() => setEditing(true)} style={chanBtnStyle('#333')} title="Changer le channel">✏️</button>
        </div>
    );
};

const chanBtnStyle = (bg) => ({
    background: bg, border: 'none', borderRadius: '6px', color: 'white',
    padding: '5px 9px', cursor: 'pointer', fontSize: '0.85rem',
});

const discordActionsStyle = {
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' },
    row: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
};

const AvailabilityModal = ({ participants, availabilities, onToggle, onClose, participantUserIds, isAdmin, weekOptions, previousWeekOption, selectedWeekKey, onChangeWeek }) => {
    const currentUser = pb.authStore.model;
    const currentUserId = currentUser?.id;
    const isActualAdmin = isAdmin || currentUser?.isAdmin;
    const [editingWeek, setEditingWeek] = useState(false);

    const selectedWeek = [previousWeekOption, ...weekOptions].find(w => w.key === selectedWeekKey) || weekOptions[0];

    const weekGrid = React.useMemo(() => {
        const nested = normalizeToNested(availabilities?.week_availabilities, weekOptions[0].key);
        return nested[selectedWeekKey] || {};
    }, [availabilities, selectedWeekKey, weekOptions]);

    // Rétrocompat : si pas de participantUserIds, tout le monde peut tout modifier
    const canToggle = (participant) => {
        if (isActualAdmin) return true;
        if (!participantUserIds || Object.keys(participantUserIds).length === 0) return true;
        return participantUserIds[participant] === currentUserId;
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} className="fade-in-up" onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h3 style={{margin: 0}} className="tvs-title-gradient">Gestion des Disponibilités</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {isActualAdmin && (
                            editingWeek ? (
                                <select
                                    autoFocus
                                    value={selectedWeekKey}
                                    onChange={e => { onChangeWeek(e.target.value); setEditingWeek(false); }}
                                    onBlur={() => setEditingWeek(false)}
                                    style={styles.weekSelect}
                                    title="Choisir la semaine"
                                >
                                    <option value={previousWeekOption.key}>{previousWeekOption.label}</option>
                                    <option disabled>──────────</option>
                                    {weekOptions.map(w => (
                                        <option key={w.key} value={w.key}>{w.label}</option>
                                    ))}
                                </select>
                            ) : (
                                <div style={styles.weekLabelRow}>
                                    <span style={styles.weekLabelText}>{selectedWeek.label}</span>
                                    <button
                                        onClick={() => setEditingWeek(true)}
                                        style={styles.weekEditBtn}
                                        className="tvs-btn-lift"
                                        title="Changer de semaine"
                                        type="button"
                                    >
                                        <Edit2 size={14}/>
                                    </button>
                                </div>
                            )
                        )}
                        {!isActualAdmin && (
                            <span style={styles.weekLabelText}>{selectedWeek.label}</span>
                        )}
                        <button onClick={onClose} style={styles.closeBtn} className="tvs-btn-lift">
                            <X size={24}/>
                        </button>
                    </div>
                </div>

                <div style={styles.modalBody}>
                    <table style={styles.table}>
                        <thead>
                        <tr>
                            <th style={styles.th}>Jour</th>
                            {participants.map(p => (
                                <th key={p} style={{...styles.th, color: canToggle(p) ? 'white' : '#555'}}>
                                    {p}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {DAYS.map((day, i) => {
                            const date = addDays(selectedWeek.monday, i);
                            const holiday = getHolidayLabel(date);
                            return (
                                <tr key={day} style={{...styles.tr, ...(holiday ? styles.holidayRow : {})}} className="tvs-row-hover">
                                    <td style={styles.td}>
                                        <strong>{day.charAt(0).toUpperCase() + day.slice(1)}</strong>
                                        <span style={styles.dayDate}>{formatShortDate(date)}</span>
                                        {holiday && <span style={styles.holidayBadge} title={holiday}>🎉 {holiday}</span>}
                                    </td>
                                    {participants.map(p => {
                                        const isAvailable = weekGrid[p]?.[day];
                                        const interactive = canToggle(p);
                                        return (
                                            <td key={p} style={{...styles.td, opacity: interactive ? 1 : 0.35}}>
                                                <div
                                                    onClick={() => interactive && onToggle(p, day, selectedWeekKey)}
                                                    style={{
                                                        cursor: interactive ? 'pointer' : 'not-allowed',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        transition: 'transform 0.1s ease'
                                                    }}
                                                    onMouseDown={(e) => interactive && (e.currentTarget.style.transform = 'scale(0.9)')}
                                                    onMouseUp={(e) => interactive && (e.currentTarget.style.transform = 'scale(1)')}
                                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                >
                                                    {isAvailable === null || isAvailable === undefined ?
                                                        <CircleQuestionMark color="#facc15" size={24} /> :
                                                        isAvailable ?
                                                            <CheckCircle color="#4ade80" size={24} /> :
                                                            <XCircle color="#f87171" size={24} />
                                                    }
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const ParticipantSelector = ({ allUsers, selected, onChange }) => {
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const selectedNames = selected.map(s => s.username);
    const filtered = allUsers
        .filter(u => !selectedNames.includes(u.username))
        .filter(u => u.username.toLowerCase().includes(search.toLowerCase()));
    const isNewGuest = search.trim() &&
        !allUsers.some(u => u.username.toLowerCase() === search.trim().toLowerCase()) &&
        !selectedNames.includes(search.trim());

    const addUser = (user) => { onChange([...selected, { username: user.username, userId: user.id }]); setSearch(''); };
    const addGuest = () => { const n = search.trim(); if (!n || selectedNames.includes(n)) return; onChange([...selected, { username: n, userId: null }]); setSearch(''); };
    const remove = (username) => onChange(selected.filter(s => s.username !== username));

    return (
        <div style={{ marginTop: '10px', position: 'relative' }}>
            <div style={selectorStyles.tags}>
                {selected.map(s => (
                    <span key={s.username} style={s.userId ? selectorStyles.tag : selectorStyles.guestTag}>
                        {s.username}
                        <button style={selectorStyles.tagRemove} onClick={() => remove(s.username)}>×</button>
                    </span>
                ))}
            </div>
            <input
                style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
                className="tvs-input"
                placeholder="Rechercher un participant ou taper un nom..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (filtered.length === 1) addUser(filtered[0]); else if (isNewGuest) addGuest(); } }}
            />
            {open && (filtered.length > 0 || isNewGuest) && (
                <div style={selectorStyles.dropdown}>
                    {filtered.map(u => (
                        <div key={u.id} style={selectorStyles.option} onMouseDown={() => addUser(u)}>
                            {u.username}
                        </div>
                    ))}
                    {isNewGuest && (
                        <div style={selectorStyles.guestOption} onMouseDown={addGuest}>
                            + Ajouter "{search.trim()}" comme invité (sans compte)
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const CreateAccountsPopup = ({ guests, onConfirm, onCancel }) => {
    const [checked, setChecked] = useState(Object.fromEntries(guests.map(g => [g, true])));
    const toggle = (name) => setChecked(c => ({ ...c, [name]: !c[name] }));
    return (
        <div style={styles.modalOverlay}>
            <div style={{ ...styles.modalContent, maxWidth: '420px' }} className="fade-in-up">
                <div style={styles.modalHeader}>
                    <h3 style={{ margin: 0 }} className="tvs-title-gradient">Créer des comptes ?</h3>
                    <button onClick={onCancel} style={styles.closeBtn} className="tvs-btn-lift"><X size={24} /></button>
                </div>
                <div style={{ padding: '20px' }}>
                    <p style={{ color: '#aaa', marginTop: 0 }}>
                        Ces participants n'ont pas de compte. Cochez ceux pour lesquels vous voulez en créer un :
                    </p>
                    {guests.map(g => (
                        <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', cursor: 'pointer', color: 'white' }}>
                            <input type="checkbox" checked={checked[g]} onChange={() => toggle(g)} style={{ width: '16px', height: '16px' }} />
                            <span>{g}</span>
                            <span style={{ color: '#666', fontSize: '0.85rem' }}>mdp : {g}123</span>
                        </label>
                    ))}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                        <button style={styles.addBtn} onClick={() => onConfirm(Object.keys(checked).filter(k => checked[k]))}>
                            <Save size={16} /> Créer et continuer
                        </button>
                        <button style={{ ...styles.addBtn, background: '#444' }} onClick={() => onConfirm([])}>
                            Ignorer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const selectorStyles = {
    tags: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' },
    tag: { background: '#ff4444', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' },
    guestTag: { background: '#555', color: '#ccc', padding: '4px 10px', borderRadius: '20px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', border: '1px dashed #888' },
    tagRemove: { background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, fontSize: '1rem', lineHeight: 1 },
    dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#1a1a1a', border: '1px solid #444', borderRadius: '5px', zIndex: 100, maxHeight: '200px', overflowY: 'auto' },
    option: { padding: '10px 14px', cursor: 'pointer', color: 'white', borderBottom: '1px solid #222' },
    guestOption: { padding: '10px 14px', cursor: 'pointer', color: '#facc15', borderTop: '1px solid #333', fontSize: '0.9rem' },
};

const styles = {
    mainContainer: {
        padding: '20px',
        maxWidth: '1400px',
        margin: '0 auto'
    },
    detailContainer: {
        color: 'white',
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
    },
    pageTitle: {
        color: 'white',
        margin: 0,
        fontSize: 'clamp(1.2rem, 4vw, 2rem)'
    },
    backBtn: {
        background: 'none',
        border: 'none',
        color: '#ff4444',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        marginBottom: '20px',
        padding: 0,
        fontSize: '1rem'
    },
    title: {
        fontSize: 'clamp(1.5rem, 5vw, 2rem)',
        marginBottom: '30px',
        wordBreak: 'break-word'
    },
    subtitle: {
        color: '#888',
        fontSize: 'clamp(1rem, 3vw, 1.2rem)'
    },
    playersRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '-18px',
        marginBottom: '28px',
        color: '#666',
    },
    playersText: {
        fontSize: '0.85rem',
        color: '#888',
    },
    section: {
        backgroundColor: '#161616',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '20px',
        border: '1px solid #333'
    },
    topRowContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
        gap: '20px',
        marginBottom: '20px'
    },
    callSection: {
        backgroundColor: '#161616',
        padding: '20px',
        borderRadius: '12px',
        border: '1px solid #333'
    },
    availSection: {
        backgroundColor: '#1a1a1a',
        padding: '20px',
        borderRadius: '12px',
        border: '2px solid #ff4444'
    },
    availHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px',
        flexWrap: 'wrap',
        gap: '10px'
    },
    availDays: {
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
        justifyContent: 'center',
        padding: '10px 0'
    },
    dayBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        backgroundColor: '#22c55e',
        color: 'white',
        padding: '8px 10px 8px 16px',
        borderRadius: '20px',
        fontWeight: 'bold',
        fontSize: '0.9rem'
    },
    dayBadgeAddBtn: {
        background: 'rgba(255, 255, 255, 0.2)',
        border: 'none',
        borderRadius: '50%',
        color: 'white',
        width: '20px',
        height: '20px',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    },
    settingsBtn: {
        background: '#444',
        color: 'white',
        border: 'none',
        padding: '8px 16px',
        borderRadius: '5px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        fontSize: '0.9rem'
    },
    sectionTitle: {
        marginBottom: '15px',
        color: '#ff4444',
        margin: 0,
        fontSize: 'clamp(1rem, 3vw, 1.2rem)'
    },
    compactForm: {
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
        marginTop: '15px'
    },
    formGroup: {
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap'
    },
    input: {
        background: '#0a0a0a',
        border: '1px solid #444',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        outline: 'none',
        flex: 1,
        minWidth: '200px',
        fontSize: '1rem'
    },
    addBtn: {
        background: '#ff4444',
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '5px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        whiteSpace: 'nowrap',
        fontSize: '1rem'
    },
    tableWrapper: {
        overflowX: 'auto',
        marginTop: '15px'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        minWidth: '600px'
    },
    th: {
        textAlign: 'left',
        padding: '12px',
        borderBottom: '2px solid #333',
        color: '#888',
        fontSize: 'clamp(0.8rem, 2vw, 1rem)'
    },
    td: {
        padding: '12px',
        borderBottom: '1px solid #222',
        fontSize: 'clamp(0.8rem, 2vw, 0.95rem)'
    },
    tr: {
        transition: 'background 0.2s',
    },
    holidayRow: {
        backgroundColor: 'rgba(255, 179, 71, 0.08)',
        boxShadow: 'inset 3px 0 0 0 #ffb347',
    },
    dayDate: {
        color: '#888',
        fontSize: '0.8rem',
        marginLeft: '8px',
        fontWeight: 'normal',
    },
    holidayBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        marginLeft: '10px',
        padding: '2px 8px',
        borderRadius: '10px',
        fontSize: '0.7rem',
        fontWeight: 'bold',
        color: '#ffb347',
        backgroundColor: 'rgba(255, 179, 71, 0.15)',
        whiteSpace: 'nowrap',
    },
    weekSelect: {
        background: '#222',
        border: '1px solid #444',
        color: 'white',
        borderRadius: '6px',
        padding: '7px 10px',
        fontSize: '0.85rem',
        outline: 'none',
        cursor: 'pointer',
    },
    weekLabelRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: '#222',
        border: '1px solid #333',
        borderRadius: '6px',
        padding: '6px 6px 6px 12px',
    },
    weekLabelText: {
        color: '#ccc',
        fontSize: '0.85rem',
        whiteSpace: 'nowrap',
    },
    weekEditBtn: {
        background: 'none',
        border: 'none',
        color: '#ff4444',
        cursor: 'pointer',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '4px',
    },
    createForm: {
        backgroundColor: '#1a1a1a',
        padding: '20px',
        borderRadius: '12px',
        border: '1px solid #ff4444',
        marginBottom: '30px',
        position: 'relative',
        zIndex: 5
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        gap: '20px'
    },
    card: {
        backgroundColor: '#161616',
        padding: '20px',
        borderRadius: '16px',
        border: '1px solid #333',
        cursor: 'pointer',
        textAlign: 'center',
        color: 'white',
        position: 'relative',
    },
    emptyState: {
        color: '#888',
        gridColumn: '1/-1',
        textAlign: 'center',
        padding: '40px',
        border: '2px dashed #333',
        borderRadius: '12px'
    },
    adminActions: {
        display: 'flex',
        justifyContent: 'center',
        gap: '10px',
        marginTop: '15px',
        borderTop: '1px solid #333',
        paddingTop: '10px'
    },
    miniBtn: {
        color: 'white',
        border: 'none',
        padding: '8px 12px',
        borderRadius: '4px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '5px'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
    },
    modalContent: {
        backgroundColor: '#161616',
        borderRadius: '12px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '1px solid #333'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px',
        borderBottom: '1px solid #333',
        position: 'sticky',
        top: 0,
        backgroundColor: '#161616',
        zIndex: 1
    },
    modalBody: {
        padding: '20px'
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        color: '#ff4444',
        cursor: 'pointer',
        padding: '5px',
        display: 'flex',
        alignItems: 'center'
    }
};

export default PartyManager;